import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { CacheService } from './CacheService.ts';
import { Profile } from '../models/Profile.ts';
import { getFileUrl } from './Directus.ts';

export interface UserData {
  id: string;
  name: string;
  avatar: string;
  avatarUrl: string;
}

class UserService {
  private cache: CacheService;
  private userCache: Map<string, UserData> = new Map();

  constructor() {
    this.cache = new CacheService();
  }

  /**
   * Fetches a user's profile data by ID, with caching
   * @param userId The user ID to fetch
   * @returns The user data or null if not found
   */
  async getUserData(userId: string): Promise<UserData | null> {
    // Check in-memory cache first
    if (this.userCache.has(userId)) {
      return this.userCache.get(userId)!;
    }

    // Check persistent cache
    const cacheKey = `user_${userId}`;
    const cached = await this.cache.get<UserData>(cacheKey);
    if (cached.value && !cached.isExpired) {
      this.userCache.set(userId, cached.value);
      return cached.value;
    }

    // Fetch from Firebase (if expired or not in cache)
    try {
      const db = getFirestore();
      const docRef = doc(db, `users/${userId}/profile/default`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Fall back to stale cached value if available
        return cached.value ?? null;
      }

      const profileData = docSnap.data() as Profile;
      const userData: UserData = {
        id: userId,
        name: profileData.name || userId,
        avatar: profileData.avatar,
        avatarUrl: getFileUrl(profileData.avatar),
      };

      // Store in caches
      this.userCache.set(userId, userData);
      await this.cache.set<UserData>(cacheKey, userData, 60 * 60 * 1000); // Cache for 1 hour

      return userData;
    } catch (error) {
      console.error('Error fetching user data:', error);
      // On error, return stale cached value if we have one
      if (cached.value) {
        this.userCache.set(userId, cached.value);
        return cached.value;
      }
      return null;
    }
  }

  /**
   * Fetches multiple users' profile data by IDs, with caching
   * @param userIds Array of user IDs to fetch
   * @returns Array of user data objects
   */
  async getUsersData(userIds: string[]): Promise<UserData[]> {
    const promises = userIds.map((id) => this.getUserData(id));
    const results = await Promise.all(promises);
    return results.filter((user): user is UserData => user !== null);
  }

  /**
   * Clears the user cache for a specific user
   * @param userId The user ID to clear from cache
   */
  async clearUserCache(userId: string): Promise<void> {
    const cacheKey = `user_${userId}`;
    this.userCache.delete(userId);
    await this.cache.remove(cacheKey);
  }
}

// Export a singleton instance
export const userService = new UserService();
